import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Rating,
  IconButton,
  Paper,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Slide
} from '@mui/material';
import {
  FormatQuote as QuoteIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  Public as PublicIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

const Testimonials = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-slide for featured testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredTestimonials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const testimonials = [
    {
      name: 'Deniz İrtegün',
      company: 'Milliirade.com',
      position: 'Genel Yayın Yönetmeni',
      rating: 5,
      comment: 'Nova Flow, performans, görsellik ve kullanım kolaylığı bakımından son derece tatmin edici bir tema. Mobil uyumlu olması ve yönetim panelinden kolayca konfigüre edilebilir olması da bizim için bir artı oldu.',
      avatar: 'DI',
      color: '#1976d2',
      category: 'Performans',
      verified: true,
      stats: { visitors: '50K+', speed: '98%' }
    },
    {
      name: 'Furkan Çam',
      company: 'Habermaras.com',
      position: 'İçerik Editörü',
      rating: 5,
      comment: 'RÖNESANS müşterilerinden gelen geri dönüş ve talepler dikkate alınarak bunların büyük bir kısmı gerçekleştirilmiş. Bizim özel yazılım olarak talep ettiğimiz özelliklerin nerdeyse tamamına yeni yazılımda yer verildiğini görmekten dolayı çok mutluyuz.',
      avatar: 'FÇ',
      color: '#2e7d32',
      category: 'Özelleştirme',
      verified: true,
      stats: { features: '100+', satisfaction: '99%' }
    },
    {
      name: 'Bülent Deniz',
      company: 'Habervakti.com',
      position: 'Site Yöneticisi',
      rating: 5,
      comment: 'Nova, "Bir haber sitesinde ne olması gerekir?" sorusu bizler için cevaplamış bir yazılım olarak karşımıza çıktı. Arkasında yılların tecrübesi var! Bu tecrübeyi yazılım, tasarım, görsellik, hız, sadelikle birleştirmiş, ama bir o kadar albenili!',
      avatar: 'BD',
      color: '#ed6c02',
      category: 'Tasarım',
      verified: true,
      stats: { experience: '10+ Yıl', design: 'A+' }
    },
    {
      name: 'Ali Çam',
      company: 'Haber46.com.tr',
      position: 'Teknik Direktör',
      rating: 5,
      comment: 'Nova, tam anlamıyla mükemmel ve esnek özellikler içeriyor. Yeni yazılımda hem panelde içerik girme kolaylığı, hem de kullanıcı arayüzündeki esneklik, geliştirilen Google optimizasyonu, tüm cihazlarla tam uyumlu responsive tasarım ile gerçekten harika.',
      avatar: 'AÇ',
      color: '#7b1fa2',
      category: 'SEO & Responsive',
      verified: true,
      stats: { seo: 'A+', mobile: '100%' }
    },
    {
      name: 'Mehmet Kaplan',
      company: 'Haberlobi.com',
      position: 'Editör',
      rating: 5,
      comment: 'Tasarım ve hız açısından şimdiye kadar yapılan en güzel tema diyebiliriz Nova için. Haber detay, ana sayfa düzeni, modülleri, kısacası sitenin her tarafı mükemmel. RÖNESANS, Nova ile bayrağı hayli yukarıya taşıdı.',
      avatar: 'MK',
      color: '#d32f2f',
      category: 'Hız & Tasarım',
      verified: true,
      stats: { speed: '<300ms', rating: '5/5' }
    },
    {
      name: 'Serkan Karaaslan',
      company: 'Kanalmaras.com',
      position: 'Genel Koordinatör',
      rating: 5,
      comment: 'Gelişen teknolojiyi yakalayan ve markamızın gelişimine önemli katkılar sağlayan Nova haber yazılımını memnuniyetle kullanmaktayız. Nova\'yı kullanmaya başladıktan sonra ekip olarak ne kadar doğru bir karar verdiğimizi çok daha iyi anlıyoruz.',
      avatar: 'SK',
      color: '#0288d1',
      category: 'Teknoloji',
      verified: true,
      stats: { growth: '+150%', tech: 'Modern' }
    }
  ];

  const featuredTestimonials = testimonials.slice(0, 3);

  const stats = [
    { icon: <BusinessIcon />, value: '500+', label: 'Aktif Müşteri', color: '#1976d2' },
    { icon: <StarIcon />, value: '4.9/5', label: 'Müşteri Memnuniyeti', color: '#f59e0b' },
    { icon: <TrendingIcon />, value: '99.9%', label: 'Uptime Garantisi', color: '#10b981' },
    { icon: <SupportIcon />, value: '7/24', label: 'Teknik Destek', color: '#ef4444' }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredTestimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
  };

  return (
    <Box ref={sectionRef} sx={{ bgcolor: '#f8fafc', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23e2e8f0" fill-opacity="0.4"%3E%3Ccircle cx="11" cy="11" r="1"/%3E%3Ccircle cx="49" cy="11" r="1"/%3E%3Ccircle cx="11" cy="49" r="1"/%3E%3Ccircle cx="49" cy="49" r="1"/%3E%3Ccircle cx="30" cy="30" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <Box textAlign="center" mb={8}>
          <Fade in={isVisible} timeout={1000}>
            <Box>
              <Chip 
                icon={<VerifiedIcon />}
                label="✨ Müşteri Deneyimleri" 
                sx={{ 
                  mb: 3, 
                  bgcolor: '#1976d2', 
                  color: 'white',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  fontSize: '0.9rem'
                }} 
              />
              <Typography 
                variant="h2" 
                gutterBottom 
                fontWeight={700}
                sx={{ 
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  color: '#1a1a1a',
                  mb: 2,
                  lineHeight: 1.2
                }}
              >
                Müşteri
                <Box component="span" sx={{ color: '#1976d2', ml: 1 }}>
                  Görüşleri
                </Box>
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 600, 
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.6,
                  fontWeight: 400,
                  opacity: 0.8
                }}
              >
                Binlerce memnun müşterimizden gerçek deneyimler
              </Typography>
            </Box>
          </Fade>
        </Box>

        {/* Stats Section */}
        <Slide direction="up" in={isVisible} timeout={1200}>
          <Grid container spacing={4} sx={{ mb: 10 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '1px solid #e2e8f0',
                    borderRadius: 4,
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      borderColor: stat.color
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: `${stat.color}15`,
                      color: stat.color,
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Typography 
                    variant="h3" 
                    fontWeight={800}
                    sx={{ color: stat.color, mb: 1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight={600}
                    sx={{ color: '#374151' }}
                  >
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Slide>

        {/* Featured Testimonial Carousel */}
        <Box sx={{ mb: 10, position: 'relative' }}>
          <Typography 
            variant="h3" 
            textAlign="center" 
            fontWeight={700}
            sx={{ mb: 6, color: '#1a1a1a' }}
          >
            Öne Çıkan Yorumlar
          </Typography>

          <Box sx={{ position: 'relative', maxWidth: 900, mx: 'auto' }}>
            {/* Navigation Buttons */}
            <IconButton
              onClick={prevSlide}
              sx={{
                position: 'absolute',
                left: -60,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'white',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                width: 50,
                height: 50,
                zIndex: 2,
                '&:hover': {
                  bgcolor: '#1976d2',
                  color: 'white',
                  transform: 'translateY(-50%) scale(1.1)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <IconButton
              onClick={nextSlide}
              sx={{
                position: 'absolute',
                right: -60,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'white',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                width: 50,
                height: 50,
                zIndex: 2,
                '&:hover': {
                  bgcolor: '#1976d2',
                  color: 'white',
                  transform: 'translateY(-50%) scale(1.1)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <ArrowForwardIcon />
            </IconButton>

            {/* Featured Testimonial Card */}
            <Fade in key={currentSlide} timeout={600}>
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: featuredTestimonials[currentSlide].color
                  }
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {/* Quote Icon */}
                  <QuoteIcon 
                    sx={{ 
                      fontSize: 60, 
                      color: `${featuredTestimonials[currentSlide].color}20`,
                      position: 'absolute',
                      top: -10,
                      right: 20
                    }} 
                  />

                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontStyle: 'italic', 
                          lineHeight: 1.7,
                          color: '#374151',
                          mb: 4,
                          fontSize: { xs: '1.2rem', md: '1.4rem' }
                        }}
                      >
                        "{featuredTestimonials[currentSlide].comment}"
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: featuredTestimonials[currentSlide].color,
                            width: 70,
                            height: 70,
                            fontSize: '1.5rem',
                            fontWeight: 800
                          }}
                        >
                          {featuredTestimonials[currentSlide].avatar}
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" fontWeight={700}>
                              {featuredTestimonials[currentSlide].name}
                            </Typography>
                            {featuredTestimonials[currentSlide].verified && (
                              <VerifiedIcon sx={{ color: '#10b981', fontSize: 20 }} />
                            )}
                          </Box>
                          <Typography variant="body1" color="text.secondary" fontWeight={600}>
                            {featuredTestimonials[currentSlide].position}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {featuredTestimonials[currentSlide].company}
                          </Typography>
                        </Box>
                      </Box>

                      <Rating 
                        value={featuredTestimonials[currentSlide].rating} 
                        readOnly 
                        size="large"
                        sx={{ mb: 2 }} 
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          bgcolor: `${featuredTestimonials[currentSlide].color}10`,
                          borderRadius: 4,
                          textAlign: 'center'
                        }}
                      >
                        <Chip
                          label={featuredTestimonials[currentSlide].category}
                          sx={{
                            bgcolor: featuredTestimonials[currentSlide].color,
                            color: 'white',
                            fontWeight: 700,
                            mb: 3
                          }}
                        />
                        <Stack spacing={2}>
                          {Object.entries(featuredTestimonials[currentSlide].stats).map(([key, value], index) => (
                            <Box key={index}>
                              <Typography variant="h6" fontWeight={700} color={featuredTestimonials[currentSlide].color}>
                                {value}
                              </Typography>
                              <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                {key}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Fade>

            {/* Slide Indicators */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
              {featuredTestimonials.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  sx={{
                    width: currentSlide === index ? 40 : 12,
                    height: 12,
                    borderRadius: 6,
                    bgcolor: currentSlide === index ? '#1976d2' : '#e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: currentSlide === index ? '#1976d2' : '#cbd5e1'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* All Testimonials Grid */}
        <Typography 
          variant="h3" 
          textAlign="center" 
          fontWeight={700}
          sx={{ mb: 6, color: '#1a1a1a' }}
        >
          Tüm Müşteri Yorumları
        </Typography>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Fade in={isVisible} timeout={1000 + index * 200}>
                <Card 
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{ 
                    height: '100%',
                    p: 3,
                    border: 'none',
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: hoveredCard === index ? 'translateY(-12px) scale(1.02)' : 'translateY(0) scale(1)',
                    boxShadow: hoveredCard === index ? '0 25px 50px rgba(0,0,0,0.15)' : '0 4px 20px rgba(0,0,0,0.08)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: testimonial.color,
                      transform: hoveredCard === index ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.4s ease'
                    }
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    {/* Quote Icon */}
                    <QuoteIcon 
                      sx={{ 
                        fontSize: 40, 
                        color: `${testimonial.color}30`,
                        mb: 2
                      }} 
                    />

                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontStyle: 'italic', 
                        lineHeight: 1.7,
                        color: '#374151',
                        mb: 3,
                        fontSize: '1rem'
                      }}
                    >
                      "{testimonial.comment}"
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: testimonial.color,
                          mr: 2,
                          width: 50,
                          height: 50,
                          fontSize: '1.1rem',
                          fontWeight: 700
                        }}
                      >
                        {testimonial.avatar}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>
                            {testimonial.name}
                          </Typography>
                          {testimonial.verified && (
                            <VerifiedIcon sx={{ color: '#10b981', fontSize: 16 }} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          {testimonial.position}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Rating value={testimonial.rating} readOnly size="small" />
                      <Chip
                        label={testimonial.category}
                        size="small"
                        sx={{
                          bgcolor: `${testimonial.color}15`,
                          color: testimonial.color,
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Testimonials;